import { hasValidatedDeterministicScreening } from '../reportGeneration/deterministicScreeningValidation';
import { reportTypeStage } from '../../config/reportTypes';
import { reportPromptContractCurrency } from '../reportGeneration/reportPromptCurrency';
import { isEligibleExecutiveSummarySource } from '../buildExecutiveSummarySourceDigest';
import { customBriefSourceFrame, customBriefSourcesAreCurrent, customBriefReportHasSourceBody, customBriefSourceHasImmutableRevision, customBriefSourceMatchesCurrentPlan } from '../reportGeneration/customBriefSourceCurrency';
import { customBriefContentMatchesCurrentInputs } from '../reportGeneration/customBriefContentCurrency';
import { customBriefPreferencesFromSite } from '../reportContent/identity';
/**
 * PATH TO GO — shared analysis plan.
 *
 * This is the decision cockpit's one analysis-lifecycle projection. Existing
 * reports remain a supported evidence carrier, but printable artifact readiness
 * is deliberately separate from semantic decision readiness. The module does
 * not launch jobs or mutate evidence; it turns persisted analyses/reports,
 * preflight state, and the Generation & Impact authority oracle into a
 * deterministic plan that every Path-to-GO surface can render.
 */
import type { DecisionStage, ReportTypeId } from '../../config/reportTypes';
import {
  getReportTypeSedCurrencySections,
} from '../../config/reportTypeSedRequirements';
import {
  GENERATED_DECISION_PRODUCT_IDS,
  getDecisionProduct,
  type DecisionProductPrerequisiteKind,
} from '../../config/decisionProducts';
import type { SiteEntity } from '../../types/site';
import type {
  PowerAvailabilityPreferences,
  ProjectIntent,
  SiteRagReport,
} from '../../types/siteRag';
import type { GenerationImpactSnapshot } from '../../types/generationImpact';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import {
  decisionAnalysisDomainSchema,
  type AnalysisReadinessDecisionV1,
  type AnalysisRevisionReferenceV1,
  type DecisionAnalysisDomain,
  type DecisionAnalysisInputEnvelopeV1,
  type DecisionAnalysisSnapshotV1,
} from '../../types/decisionAnalysis';
import { validateGenerationLayoutExhibitSnapshot } from '../generationImpact/generationLayoutExhibit';
import { sameTestFitAuthorityRef } from '../generationImpact/testFitAuthority';
import {
  evaluatePersistedTestFitWorkflow,
  type TestFitWorkflowCode,
  type TestFitWorkflowReadiness,
} from '../generationImpact/testFitWorkflow';
import { hashProjectIntent } from '../sed/shouldBootstrap';
import { computeSedDirtySetSinceReport } from '../sed/sedFingerprint';
import { SHARED_DOCUMENT_SED_INPUTS, computeSedSectionContentHashesV2, sharedDocumentEvidenceMatches, buildReportSedMaterialDependencyManifest, verifyReportSedMaterialDependencyManifest, reportSedMaterialDependencyDrift } from '../decisionAnalysis/sedContentIdentity';
import { evaluateDecisionAnalysisReadiness, evaluateReportSemanticReadiness } from '../decisionAnalysis/readiness';
import { decisionAnalysisInputDrift } from '../decisionAnalysis/identity';
import { classifyAutoRebindDrift } from '../decisionAnalysis/autoRebindGate';
import { truthQuestionCountOf } from '../decisionAnalysis/lifecyclePresentation';
import { hasIndependentReportContent, isAuditAdvisoryDeliveryReason } from '../reportGeneration/evaluateReportDelivery';
import {
  WITHHELD_NARRATIVE_DECISION_HOLD_MESSAGE,
  reportHasWithheldNarrative,
} from '../report/withheldNarrative';
import type { OpenAuditFindings, OpenAuditKind } from '../audit/openAuditFlag';
import { openAuditFindings } from '../audit/openAuditFlag';
import { isGenerationImpactReportType } from '../generationImpact/reportProjection';
import { enqueueCoverageRefusalFor } from '../audit/enqueueCoverageGate';
import {
  evaluateReportAuthorityReadiness,
  formatFinancialDecisionMetricGaps,
  formatReportAuthorityBlockers,
  evaluateGenerationImpactReportReadiness,
} from '../reportGeneration/reportAuthorityReadiness';
import {
  resolveAnalysisApplicability,
  type AnalysisApplicabilityReasonCode,
  type AnalysisApplicabilityRole,
  type AnalysisEligibilityGroup,
} from './analysisApplicability';

export type ReportLifecycle =
  | 'needs-input'
  | 'dependency-blocked'
  | 'ready'
  | 'queued'
  | 'generating'
  | 'preliminary'
  | 'preview'
  | 'current'
  | 'refresh-needed'
  | 'quality-held'
  | 'failed'
  | 'failed-coverage'
  | 'unavailable';

/** Analysis-first name; `ReportLifecycle` remains the persisted/API alias. */
export type AnalysisLifecycle = ReportLifecycle;

/**
 * Currency and calculation usability are deliberately separate. A completed,
 * readable report can remain a saved calculation input while a refresh is
 * queued, running, failed, or required. `current` means the calculation input
 * matches today's authority; `saved` means an older successful input is being
 * used; `none` means no safe calculation carrier exists.
 */
export type ReportCalculationStatus = 'current' | 'saved' | 'none';

export type ReportPlanRole = AnalysisApplicabilityRole;

/** Analysis-first name; `ReportPlanRole` remains backward compatible. */
export type AnalysisPlanRole = ReportPlanRole;

export type PathAction =
  | {
      kind: 'intent';
      label: string;
      field?: string;
      /** Open the Design & report assumptions view (Stage 2 decisions). */
      view?: 'design';
    }
  | {
      kind: 'site-boundary';
      label: string;
    }
  | {
      kind: 'evidence';
      label: string;
      targetId?: string;
    }
  | {
      kind: 'report-setup';
      label: string;
      reportType: ReportTypeId;
    }
  | {
      kind: 'report-progress';
      label: string;
      reportType: ReportTypeId;
    }
  | {
      /** Open the exact readable analysis in Findings (never the Audit queue). */
      kind: 'analysis-open';
      label: string;
      reportType: ReportTypeId;
      recordId: string;
    }
  | {
      kind: 'report-qa';
      label: string;
      reportType: ReportTypeId;
      reportId?: string;
    }
  | {
      kind: 'generation-impact';
      label: string;
      focus: 'scenario' | 'save' | 'promotion' | 'layout';
    }
  | {
      kind: 'sed-rebuild';
      label: string;
      mode: 'start' | 'status' | 'retry';
    }
  | {
      kind: 'bom';
      label: string;
    }
  | {
      kind: 'financial-inputs';
      label: string;
    }
  | {
      kind: 'engineering-source';
      label: string;
      simulationId?: string;
      eoRunId?: string;
    }
  | {
      kind: 'audit';
      label: string;
      findingId?: string;
    }
  | {
      kind: 'request-access';
      label: string;
      destination: 'intent' | 'evidence' | 'reports' | 'generation-impact' | 'sed';
    }
  | {
      kind: 'none';
      label: string;
    };

export type ReportDependencyStatus = 'complete' | 'current' | 'pending' | 'blocked';

export interface ReportDependencyStep {
  id: 'configure' | 'save' | 'promote' | 'publish' | 'sed' | 'test-fit';
  label: string;
  status: ReportDependencyStatus;
}

export interface ReportAuthorityState {
  kind: 'general' | 'generation-impact';
  status: 'not-applicable' | 'valid' | 'missing' | 'stale' | 'unavailable';
  detail: string;
  workflowCode?: TestFitWorkflowCode;
  revision?: {
    scenarioId?: string;
    scenarioName?: string;
    scenarioUpdatedAt?: string;
    layoutPublicationId?: string;
    layoutVersion?: number;
    sedId?: string;
    sedVersion?: number;
  };
}

/**
 * The prerequisite tokens each product-level prerequisite kind expands to.
 *
 * TOTAL over `DecisionProductPrerequisiteKind` by type, which is the point: a
 * new kind does not compile until it says what it requires. This replaced a
 * hand-rolled ternary chain where every added kind needed a new branch, and a
 * kind without one fell silently through to `['current-sed']` — declaring the
 * SED as its only prerequisite and so reading as ready the moment the SED was
 * fresh, no matter what it actually needed.
 *
 * `sed-preflight`, `test-fit-authority` and `live-rollup` map to
 * `['current-sed']` deliberately rather than by omission: the first is
 * genuinely SED-only, and the latter two carry their authority through
 * dedicated paths (the Generation & Impact override below for Test Fit, the
 * live rollup for the Decision Guide) rather than through this token list.
 */
const PREREQUISITE_TOKENS: Record<DecisionProductPrerequisiteKind, readonly string[]> = {
  'sed-preflight': ['current-sed'],
  'test-fit-authority': ['current-sed'],
  'live-rollup': ['current-sed'],
  'financial-model': ['financial-model'],
  'equipment-bom': ['equipment-bom'],
  'custom-brief-inputs': ['custom-brief-inputs'],
  'declared-load': ['declared-load'],
};

export interface ReportPlanItem {
  type: ReportTypeId;
  /**
   * Two-stage pipeline membership (registry); absent for the executive summary
   * and for an additional analysis outside both stages (Public Sentiment).
   */
  stage?: DecisionStage;
  role: ReportPlanRole;
  lifecycle: ReportLifecycle;
  prerequisites: readonly string[];
  authority: ReportAuthorityState;
  stale: boolean;
  satisfiesGo: boolean;
  /** Always populated by `buildReportPlan`; optional for persisted legacy snapshots. */
  calculationStatus?: ReportCalculationStatus;
  /** Exact saved report revision used for calculations, which may differ from `reportId`. */
  calculationReportId?: string;
  /** Positive decision/scoring evidence. Coverage-only screening deliberately stays false. */
  contributesToCalculations?: boolean;
  errorCode?: string;
  terminalReason?: string;
  repairState?: 'resumable' | 'completed' | 'failed';
  blockedByProviderCoverage?: boolean;
  /**
   * Saved screening can complete source collection; calculationStatus
   * separately records currency. For Public Sentiment (an additional analysis)
   * this is the scan task's own completion — lifecycle only, never a stage.
   */
  screeningComplete?: boolean;
  blockingReason?: string;
  /** Missing generic source sections that reduce confidence but do not gate a screening run. */
  sourceImprovementCount?: number;
  reportId?: string;
  /**
   * ANSWERABLE truth questions on the carrier report. Travels on the plan item
   * because presentation surfaces (Path to GO's GenerateStage) receive the plan
   * WITHOUT the report objects, so they cannot derive answerability themselves —
   * which is exactly how Path to GO ended up painting a held-but-answerable
   * analysis as red FAILED while Findings showed amber Held for the same data.
   * Consumed via `lifecyclePresentation(lifecycle, truthQuestionCount)`.
   */
  truthQuestionCount?: number;
  /**
   * The auto-rebind cap has given up on this domain, so nothing will refresh it
   * automatically. Travels on the plan item for the same reason as
   * `truthQuestionCount`: Path to GO renders from the plan WITHOUT the pointer,
   * so it cannot see the give-up itself — and a bare "Refresh required" tells
   * the reader to wait for a refresh that will never arrive, while Findings
   * says the opposite for the same data.
   */
  autoRefreshExhausted?: boolean;
  /**
   * The judged analysis is stale ONLY because optional currency evidence
   * (public sentiment, economic evidence) changed after it — newer evidence is
   * available, and nothing re-runs it automatically (autoRebindGate: the
   * invalidation reaper and finalize skip exactly this drift). Presentation
   * only: readiness, `stale` and the lifecycle are unchanged.
   */
  optionalEvidenceOnly?: boolean;
  /**
   * Gate mode only (2026-09-18): the row's saved report passes every report
   * check, but no current decision analysis is loaded for it. The row reads
   * "Refresh required" (`lifecycle: 'refresh-needed'`) although nothing is
   * known to be stale (`stale` stays false), so it carries no
   * `blockingReason` — the New Report wizard renders any blocking reason as a
   * refusal. Set only for types with a decision-analysis domain.
   *
   * DISPLAY AND NEXT-STEP ORDER ONLY — never a gate input. Next-action ranks
   * such a row where it ranked as `'ready'` before the relabel (plan order,
   * with never-run rows), so the cockpit and Path to GO › Analyse keep naming
   * the same next step; it also words the headline and detail line.
   */
  savedWithoutCurrentAnalysis?: boolean;
  /**
   * AUDIT-ADVISORY (2026-08-22): the quality audit flagged this report (hold
   * stamp or reviewer needs-revision). Decision-currency is unaffected; the
   * verdict surfaces render one subtle "audit recommended" caveat from this.
   */
  auditRecommended?: boolean;
  /**
   * WHAT THE FLAG ABOVE IS MADE OF — present exactly when `auditRecommended`
   * is (2026-08-25, round 9). `'flagged'` means the report is flagged and
   * NOTHING today's rules still produce stands against it: the disclosure
   * carries the never-disclose-zero floor of 1 and itemises nothing. A surface
   * that puts the flag in a SENTENCE ("review their findings", "open it to
   * review them") must branch on this, or it promises a list the analysis
   * cannot produce — the defect round 8 closed on the four delivery surfaces
   * and missed on the Findings Dossier's chip tooltip and rollup line.
   *
   * CONSUMERS OF `auditRecommended`, as of this change — a grep of the tree,
   * not a recollection:
   *   - `FindingsDossier` chip tooltip + hero rollup — branch on `auditKind`.
   *   - `pathToGo/DecisionStage.tsx` ("Audit recommended on N analyses —
   *     findings included at reduced confidence") — STILL makes the findings
   *     claim on a flagged-floor analysis. Out of this change's file set; the
   *     fix is the same branch, and the data is now on the plan item.
   *   - `decisionSnapshot.ts` — deliberately does NOT carry the kind; see the
   *     record-identity note there.
   * `ReportHeaderStrip`'s export dialog reads `openAuditFindings` directly and
   * already branches on `kind` (verified in source, not inherited).
   */
  auditKind?: OpenAuditKind;
  /** Immutable semantic analysis selected as the decision evidence carrier. */
  analysisId?: string;
  analysisInputHash?: string;
  analysisContentHash?: string;
  analysisSnapshotPath?: string;
  analysisCreatedAt?: string;
  analysisInput?: DecisionAnalysisInputEnvelopeV1;
  analysisReadiness?: AnalysisReadinessDecisionV1;
  analysisReferenceValid?: boolean;
  decisionEvidenceSource?: 'semantic-analysis' | 'legacy-report';
  primaryAction: PathAction;
  /**
   * User-facing analysis lane, with the reason and exact action needed next.
   * `nextAction` is the same object as `primaryAction` so surfaces cannot
   * disagree about where a preparation or launch control goes.
   */
  eligibility?: {
    group: AnalysisEligibilityGroup;
    reasonCode: AnalysisApplicabilityReasonCode;
    reason: string;
    nextAction: PathAction;
  };
  dependencyChain?: readonly ReportDependencyStep[];
}

/** Analysis-first name for new callers without breaking report-plan consumers. */
export type AnalysisPlanItem = ReportPlanItem;

export type ReportRuntimeLifecycle = 'queued' | 'generating' | 'failed' | 'failed-coverage';

/** Runtime jobs still use legacy report job states underneath. */
export type AnalysisRuntimeLifecycle = ReportRuntimeLifecycle;

export interface ReportSatisfactionContext {
  site: SiteEntity;
  sed: SiteEvidenceDocument | null;
  intent?: ProjectIntent;
  /** Decision-defining strategy in force for this evaluation. */
  powerAvailabilityPreferences?: PowerAvailabilityPreferences;
  requiresGenerationImpact: boolean;
  testFitReadiness?: TestFitWorkflowReadiness;
  /**
   * Semantic readiness replaces printable delivery only in the final gate
   * rollout mode. Omitted/false preserves legacy report delivery behavior.
   */
  analysisGateEnabled?: boolean;
}

/** Analysis-first name for the same SED/intent/authority gate context. */
export type AnalysisSatisfactionContext = ReportSatisfactionContext;

/** Loaded immutable analysis plus its persisted locator. */
export interface PathToGoAnalysisSnapshot {
  revision: AnalysisRevisionReferenceV1;
  snapshot: DecisionAnalysisSnapshotV1;
  /**
   * This entry is the pointer's held/degraded LATEST ATTEMPT, selected by
   * `latestAttemptJudgedOnInput` (lib/decisionAnalysis/browserProjection) —
   * the same basis the invalidation reaper judges. Readiness selects it over
   * the pointer's older passing reference and judges currency on ITS input.
   * Set by the loaders only; never ready by construction.
   */
  judgedLatestAttempt?: boolean;
}

function latestReportOfType(
  reports: readonly SiteRagReport[],
  type: ReportTypeId,
): SiteRagReport | null {
  const matches = reports
    .filter((report) => (report.reportType ?? 'feasibility') === type)
    .sort((left, right) => Date.parse(right.createdAt ?? '') - Date.parse(left.createdAt ?? ''));
  return matches[0] ?? null;
}

/**
 * The newest row is the lifecycle/display carrier, but a failed or partial
 * retry must not displace the last completed revision from calculations.
 */
export function latestUsableSavedReportOfType(
  reports: readonly SiteRagReport[],
  type: ReportTypeId,
): SiteRagReport | null {
  const matches = reports
    .filter((report) => (report.reportType ?? 'feasibility') === type)
    .sort((left, right) => Date.parse(right.createdAt ?? '') - Date.parse(left.createdAt ?? ''));
  return matches.find((report) => reportCanContributeSavedResult(report, type)) ?? null;
}

function decisionAnalysisDomain(
  type: ReportTypeId,
): DecisionAnalysisDomain | null {
  const parsed = decisionAnalysisDomainSchema.safeParse(type);
  return parsed.success ? parsed.data : null;
}

function latestAnalysisOfType(
  analyses: readonly PathToGoAnalysisSnapshot[],
  type: ReportTypeId,
): PathToGoAnalysisSnapshot | null {
  const domain = decisionAnalysisDomain(type);
  if (!domain) return null;
  const matches = analyses
    .filter(entry => entry?.snapshot?.domain === domain && entry?.revision != null);
  // The judged latest attempt IS the pointer's committed outcome at rest, by
  // commit order. Its snapshot `createdAt` can predate the passing reference's
  // when two runs overlapped, so it must not lose the newest-first sort below:
  // the reaper judges this entry, and readiness has to judge the same one.
  const judged = matches.find(entry => entry.judgedLatestAttempt === true);
  if (judged) return judged;
  return matches
    .sort((left, right) =>
      Date.parse(right.revision?.createdAt ?? '') - Date.parse(left.revision?.createdAt ?? ''))[0] ?? null;
}

function analysisReferenceMatchesSnapshot(
  entry: PathToGoAnalysisSnapshot,
): boolean {
  const { revision, snapshot } = entry;
  if (!revision || !snapshot) return false;
  return Boolean(
    revision.analysisId === snapshot.analysisId
      && revision.siteId === snapshot.siteId
      && revision.domain === snapshot.domain
      && revision.inputHash === snapshot.inputHash
      && revision.contentHash === snapshot.contentHash
      && revision.createdAt === snapshot.createdAt
      && typeof revision.snapshotPath === 'string'
      && revision.snapshotPath.trim().length > 0,
  );
}

const PRESENTATION_ONLY_DELIVERY_BLOCKS = new Set([
  'artifact-unavailable',
  'layout-pending',
  'layout-invalid',
  'projection-incomplete',
]);

/**
 * True when a V2 report carries an immutable semantic analysis identity.
 *
 * The presentation snapshot and PDF/DOCX/PPTX pointers are intentionally not
 * part of this predicate. They are optional projections of the semantic
 * revision and must not gate a live decision.
 */
export function reportHasSemanticAnalysisRevision(report: SiteRagReport): boolean {
  const revision = report.reportRevision;
  if (!revision) return false;
  const hash = (value: unknown): boolean =>
    typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
  const nonEmpty = (value: unknown): boolean =>
    typeof value === 'string' && value.trim().length > 0;
  return Boolean(
    nonEmpty(revision.revisionId)
      && nonEmpty(revision.sourcePath)
      && hash(revision.sourceHash)
      && nonEmpty(revision.documentPath)
      && hash(revision.documentHash),
  );
}

/** Codes evaluateReportDelivery emitted AHEAD of the retired withheld reason. */
const REASON_CODES_BEFORE_WITHHELD = new Set(['unauthorized', 'expired', 'metadata-only', 'body-unavailable', 'degraded', 'draft']);

/**
 * True for a reason evaluateReportDelivery emitted AHEAD of the retired
 * withheld reason. 'degraded' and 'draft' are emitted on both sides of it:
 * the missing-reviewer, tier and degraded-mode reasons came before, and the
 * frozen document's and artifact manifest's quality-state reasons ("The
 * document revision …", "The artifact manifest …") after. Placing by code
 * alone put a withheld report with only a document-state 'degraded' reason
 * in the 'failed' lifecycle, where the base stamp read 'quality-held'.
 */
function emittedBeforeWithheld(reason: { code: string; message: string }): boolean {
  if (!REASON_CODES_BEFORE_WITHHELD.has(reason.code)) return false;
  return !((reason.code === 'degraded' || reason.code === 'draft')
    && /^The (?:document revision|artifact manifest) /.test(reason.message));
}

/**
 * WITHHELD NARRATIVE × GO — deliberately UNCHANGED by the 2026-09-18 export
 * decision ("Allow export"). Until then a withheld narrative leaf minted a
 * non-advisory 'hard-gate-failed' DELIVERY reason, and this module derived GO
 * eligibility from the stamped delivery decision: the report path kept a
 * withheld report out of GO (lifecycle 'quality-held'); gate mode decided GO
 * from the frozen analysis alone ('held' is decision-eligible). Delivery no
 * longer carries that reason, and how a withheld narrative counts toward GO
 * is an open owner question, so the positive-decision reads below see the
 * delivery decision WITH the hold reinstated — at the position the retired
 * reason held, under a message no audit-advisory predicate matches. Every
 * Path-to-GO outcome for a withheld report is therefore exactly what it was.
 * Only the GO reads use this view; delivery, export and publishing never do.
 */
function reportForDecisionEligibility(report: SiteRagReport): SiteRagReport {
  const revision = report.reportRevision;
  const decision = revision?.deliveryDecision;
  if (!revision || !decision || !Array.isArray(decision.reasons)) return report;
  // Body record OR the persisted hold finding: a metadata row that was never
  // hydrated has only the latter, and must not count toward GO either.
  if (!reportHasWithheldNarrative(report)) return report;
  if (decision.reasons.some((reason) => reason.message === WITHHELD_NARRATIVE_DECISION_HOLD_MESSAGE)) return report;
  const at = decision.reasons.findIndex((reason) => !emittedBeforeWithheld(reason));
  const insertAt = at < 0 ? decision.reasons.length : at;
  const reasons = [
    ...decision.reasons.slice(0, insertAt),
    { code: 'hard-gate-failed' as const, message: WITHHELD_NARRATIVE_DECISION_HOLD_MESSAGE, overrideable: false },
    ...decision.reasons.slice(insertAt),
  ];
  return {
    ...report,
    reportRevision: {
      ...revision,
      deliveryDecision: {
        ...decision,
        state: decision.state === 'ready' ? 'view-only' : decision.state,
        canExport: false,
        canPublish: false,
        reasons,
      },
    },
  };
}

/**
 * A semantic revision may be decision-ready while its optional printable
 * projection is still pending or failed. All evidence/body/quality blockers
 * remain fail-closed; only presentation-specific delivery reasons are ignored.
 */
function semanticAnalysisHasDecisionHold(reportInput: SiteRagReport): boolean {
  // Both callers pass the eligibility view already; this one is defence in
  // depth for a future caller (idempotent: the reinstated reason is found).
  const report = reportForDecisionEligibility(reportInput);
  if (evaluateReportSemanticReadiness(report).holdReason !== null) return true;
  const decision = report.reportRevision?.deliveryDecision;
  if (!decision || decision.state === 'ready') return false;
  if (decision.canViewPrivate === false) return true;
  if (!Array.isArray(decision.reasons) || decision.reasons.length === 0) {
    return true;
  }
  return decision.reasons.some(
    (reason) => !PRESENTATION_ONLY_DELIVERY_BLOCKS.has(reason.code)
      // Stamped audit outcomes on historical immutable revisions are
      // audit-advisory (2026-08-22) — 'quality-held' AND the four truth-path
      // 'hard-gate-failed' messages (post-08-05 holds stamped BOTH).
      && !isAuditAdvisoryDeliveryReason(reason),
  );
}

function reportHasDeliveryHold(
  reportInput: SiteRagReport,
  analysisGateEnabled = false,
): boolean {
  const report = reportForDecisionEligibility(reportInput);
  if (evaluateReportSemanticReadiness(report).holdReason !== null) return true;
  const hasSemanticRevision = reportHasSemanticAnalysisRevision(report);
  return Boolean(
    // isDraft with a hold stamp is the hold era's export-bar MIRROR, not a
    // draft (2026-08-22) — every pre-branch held report carries it, and it
    // must not exclude them from the verdict.
    (report.isDraft && report.qualityHold !== 'needs-revision')
    || report.generationTier === 'draft'
    || (
      report.reportRevision
      && (
        (analysisGateEnabled || hasIndependentReportContent(report.pdfDelivery, report.reportRevision.deliveryDecision)) && hasSemanticRevision
          ? semanticAnalysisHasDecisionHold(report)
          // Legacy read of the stamped decision: a revision whose ONLY
          // delivery reasons are audit-advisory 'quality-held' stamps is
          // decision-current (2026-08-22) — any other reason still holds.
          : report.reportRevision.deliveryDecision.state !== 'ready'
            && (
              report.reportRevision.deliveryDecision.canViewPrivate === false
              || !Array.isArray(report.reportRevision.deliveryDecision.reasons)
              || report.reportRevision.deliveryDecision.reasons.length === 0
              || report.reportRevision.deliveryDecision.reasons.some(
                (reason) => !isAuditAdvisoryDeliveryReason(reason),
              )
            )
      )
    )
    // AUDIT-ADVISORY (2026-08-22, William): quality holds and reviewer
    // verdicts no longer disqualify a report from the decision — the verdict
    // computes WITH the held report (its findings cap normally, the composite
    // charges its 0.5 confidence discount) and the surfaces carry a subtle
    // audit-pending caveat instead. degradedMode/requiresRegeneration remain:
    // those are data-currency defects, not audit process.
    || report.degradedMode
    || report.requiresRegeneration,
  );
}

/**
 * AUDIT-ADVISORY resolution for the plan item: the report is decision-current
 * but its quality audit flagged it (a stamped hold, a reviewer needs-revision
 * verdict, or blocking truth violations). Surfaces render this as one subtle
 * "audit recommended" note — never a blocker, never a lifecycle state.
 *
 * RETURNS THE WHOLE RESOLUTION, NOT THE BOOLEAN (round 9). The flag alone let
 * the Findings Dossier's chip tooltip and its rollup line assert that findings
 * stand on a FLAGGED-FLOOR analysis — every stamp superseded, `findings` empty,
 * `count` the never-disclose-zero floor — the same untruth the four delivery
 * surfaces stopped telling in round 8. `kind` is what those two surfaces branch
 * on; `flag` is unchanged (`openAuditFindings(report).flag` is
 * `reportHasOpenAuditFlag(report)` by construction), so no row gains or loses
 * its "Audit recommended" chip because of this.
 */
function reportAuditRecommendation(report: SiteRagReport): OpenAuditFindings | null {
  const audit = openAuditFindings(report);
  return audit.flag ? audit : null;
}

function reportHasDecisionReadyRevision(
  reportInput: SiteRagReport,
  analysisGateEnabled = false,
): boolean {
  const report = reportForDecisionEligibility(reportInput);
  if (evaluateReportSemanticReadiness(report).holdReason !== null) return false;
  const revision = report.reportRevision;
  if ((analysisGateEnabled || (revision && hasIndependentReportContent(report.pdfDelivery, revision.deliveryDecision)))
    && reportHasSemanticAnalysisRevision(report)) {
    return !semanticAnalysisHasDecisionHold(report);
  }
  return Boolean(
    revision
      && (
        revision.deliveryDecision.state === 'ready'
        // AUDIT-ADVISORY (2026-08-22): a historical revision stamped
        // view-only SOLELY by 'quality-held' reasons is decision-ready —
        // the stamp is immutable, so this is read-side by necessity.
        || (
          revision.deliveryDecision.canViewPrivate !== false
          && Array.isArray(revision.deliveryDecision.reasons)
          && revision.deliveryDecision.reasons.length > 0
          && revision.deliveryDecision.reasons.every(isAuditAdvisoryDeliveryReason)
        )
      )
      && revision.sourcePath
      && revision.sourceHash,
  );
}

/**
 * A saved report may be stale and still be a legitimate calculation input.
 * This predicate intentionally ignores only currency overlays
 * (`degradedMode`/`requiresRegeneration`); it remains fail-closed for an
 * unhydrated body, private-access denial, malformed immutable identity,
 * draft/never-finalized output, and withheld narrative.
 */
export function reportCanContributeSavedResult(
  report: SiteRagReport | null | undefined,
  type: ReportTypeId,
): report is SiteRagReport {
  if (!report || (report.reportType ?? 'feasibility') !== type) return false;
  if (report._metadataOnly || typeof report.summary !== 'string') return false;
  if (
    report.generationTier === 'draft'
    || (report.isDraft === true && report.qualityHold !== 'needs-revision')
    || reportHasWithheldNarrative(report)
  ) return false;
  if (
    type === 'financial'
    && Boolean(report.financialAnalysis?.illustrativeAssumptionsNote?.trim())
  ) return false;

  const revision = report.reportRevision;
  const nonEmpty = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;
  const hash = (value: unknown): value is string =>
    typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
  if (!revision || !nonEmpty(revision.sourcePath) || !hash(revision.sourceHash)) {
    return false;
  }

  // A V2-shaped identity cannot fall through the more permissive legacy
  // source-only branch when one of its immutable fields is corrupt/missing.
  const hasV2Identity = revision.schemaVersion === 'report-revision-ref-v1'
    || revision.documentPath != null
    || revision.documentHash != null
    || revision.revisionId != null;
  if (hasV2Identity && !reportHasSemanticAnalysisRevision(report)) return false;

  const decision = revision.deliveryDecision;
  if (decision.canViewPrivate === false) return false;
  if (decision.reasons?.some((reason) => (
    reason.code === 'unauthorized'
    || reason.code === 'expired'
    || reason.code === 'metadata-only'
    || reason.code === 'body-unavailable'
  ))) return false;

  // The decision-ready revision check reads the immutable reference and its
  // stamped delivery decision; it does not read the mutable currency overlays
  // (`degradedMode` / `requiresRegeneration`).
  if (reportHasIncompleteDecisionAnalysis(report, type)) return false;
  return reportHasDecisionReadyRevision(report, true);
}

/**
 * True when a report carries a partial or malformed embedded `decisionAnalysis`
 * record (missing `input`, `input.sed`, required SED identity fields, or
 * a missing/corrupt `materialDependencies` manifest when the report is
 * material-dependency stamped). Compacted in-memory portfolio rows
 * (`_portfolioSlimBody: true`) deliberately strip `decisionAnalysis.input`
 * and are evaluated via their preserved top-level stamps unless their
 * remaining embedded fields are themselves malformed.
 */
function reportHasIncompleteDecisionAnalysis(
  report: SiteRagReport | null | undefined,
  type: ReportTypeId,
): boolean {
  if (!report || report.decisionAnalysis === undefined) return false;
  const raw = report.decisionAnalysis as unknown;
  if (!raw || typeof raw !== 'object') return true;
  const isPortfolioSlimBody =
    (report as { _portfolioSlimBody?: unknown })._portfolioSlimBody === true;
  const input = (raw as { input?: unknown }).input;
  if (!input || typeof input !== 'object') {
    return !isPortfolioSlimBody;
  }
  const sed = (input as { sed?: unknown }).sed;
  if (!sed || typeof sed !== 'object') return true;
  const sedRecord = sed as Record<string, unknown>;
  if (
    typeof sedRecord.id !== 'string'
    || !sedRecord.id.trim()
    || typeof sedRecord.version !== 'number'
    || typeof sedRecord.contentHash !== 'string'
    || !/^[a-f0-9]{64}$/.test(sedRecord.contentHash)
  ) {
    return true;
  }
  const material = sedRecord.materialDependencies;
  if (material !== undefined && !verifyReportSedMaterialDependencyManifest(material, type)) {
    return true;
  }
  if (
    report.sedMaterialDependenciesAtGen !== undefined
    && (
      !verifyReportSedMaterialDependencyManifest(report.sedMaterialDependenciesAtGen, type)
      || !material
      || !verifyReportSedMaterialDependencyManifest(material, type)
      || report.sedMaterialDependenciesAtGen.contentHash !== material.contentHash
    )
  ) {
    return true;
  }
  return false;
}

/** Material equivalence is proved against the loaded current owner SED, never
 * against a matching collection counter or an older frozen source echo. */
function reportSedMaterialDependenciesAreCurrent(
  report: SiteRagReport,
  site: SiteEntity,
  sed: SiteEvidenceDocument | null,
  type: ReportTypeId,
): boolean {
  const frozen = report.sedMaterialDependenciesAtGen;
  const sedSiteId = (sed as (SiteEvidenceDocument & { siteId?: unknown }) | null)?.siteId;
  if (!verifyReportSedMaterialDependencyManifest(frozen, type)
    || !sed || report.siteId !== site.id
    || !site.sedId || site.sedVersion == null
    || sed.id !== site.sedId || sed.version !== site.sedVersion
    || (sedSiteId != null && sedSiteId !== site.id)
    || (type === 'custom-brief' && frozen.scope !== 'full-document')) return false;
  return reportSedMaterialDependencyDrift(frozen, buildReportSedMaterialDependencyManifest(
    sed, type, { fullDocument: frozen.scope === 'full-document' },
  )).current;
}

/** The immutable written body must bind the same independently current
 * semantic input; a report-type pointer alone is never a substitute. */
function reportHasCanonicalCurrentSedInputs(
  report: SiteRagReport,
  type: ReportTypeId,
  currentInput: DecisionAnalysisInputEnvelopeV1 | undefined,
): boolean {
  const stamped = report.sedMaterialDependenciesAtGen;
  const snapshot = report.decisionAnalysis;
  const ref = report.analysisRevision;
  const snapshotSed = snapshot?.input?.sed;
  if (!currentInput || !snapshot || !ref || !snapshotSed
    || report.siteId !== currentInput.siteId
    || snapshot.domain !== decisionAnalysisDomain(type)
    || !analysisReferenceMatchesSnapshot({ revision: ref, snapshot })) return false;
  const snapshotMaterial = snapshotSed.materialDependencies;
  if (stamped !== undefined && (
    !verifyReportSedMaterialDependencyManifest(stamped, type)
      || !snapshotMaterial
      || !verifyReportSedMaterialDependencyManifest(snapshotMaterial, type)
      || stamped.contentHash !== snapshotMaterial.contentHash
  )) return false;
  return evaluateDecisionAnalysisReadiness(snapshot, currentInput, { evaluatedAt: ref.createdAt }).ready;
}

function reportSedIsCurrent(
  report: SiteRagReport,
  site: SiteEntity,
  sed: SiteEvidenceDocument | null,
  type: ReportTypeId,
): boolean {
  if (reportHasIncompleteDecisionAnalysis(report, type)) return false;
  // A present but malformed manifest cannot fall through to version equality.
  // Legacy rows with no new stamp keep all their original checks below.
  if (report.sedMaterialDependenciesAtGen !== undefined) {
    return reportSedMaterialDependenciesAreCurrent(report, site, sed, type);
  }
  if (sed && !sharedDocumentEvidenceMatches(
    report.sedSharedDocumentHashesAtGen ?? report.decisionAnalysis?.input?.sed?.sectionHashes,
    computeSedSectionContentHashesV2(sed, SHARED_DOCUMENT_SED_INPUTS),
  )) return false;
  const exactPointerMatch = Boolean(
    report.sedId
      && site.sedId
      && report.sedId === site.sedId
      && report.sedVersionUsed != null
      && site.sedVersion != null
      && report.sedVersionUsed === site.sedVersion,
  );
  if (exactPointerMatch) return true;

  // A new source should stale only analyses that actually read the changed
  // SED section. New reports carry a per-section fingerprint snapshot; compare
  // it with the live SED and keep unrelated analyses decision-current. Legacy
  // reports without those fingerprints retain the conservative pointer gate.
  if (!sed || !report.sedSectionFingerprintsAtGen) return false;
  const dirty = computeSedDirtySetSinceReport(report.sedSectionFingerprintsAtGen, sed);
  // Canonical required sections + currency bindings, minus the volatile live
  // indices (airQuality) that never decide currency — see the config docblock.
  const relevantSections = getReportTypeSedCurrencySections(type);
  return dirty.dirtySections.every(section => !relevantSections.has(section));
}

export function reportIntentIsCurrent(
  report: SiteRagReport,
  applicableIntent: ProjectIntent | undefined,
): boolean {
  if (!applicableIntent) return report.projectIntentAtGen == null;
  if (!report.projectIntentAtGen) return false;
  if ((report.projectIntentAtGen.screeningMode ?? undefined) !== (applicableIntent.screeningMode ?? undefined)) {
    return false;
  }
  return hashProjectIntent(report.projectIntentAtGen) === hashProjectIntent(applicableIntent);
}

/**
 * Report types whose analysis basis changes when the accountable grid/BTM
 * strategy changes. These mirror the native power report plus the consumers
 * declared in CROSS_REPORT_VISIBILITY.
 */
const POWER_STRATEGY_REPORT_TYPES = new Set<ReportTypeId>([
  'feasibility',
  'power-availability',
  'test-fit',
  'environmental',
  'interconnection',
]);

function reportPowerStrategyIsCurrent(
  report: SiteRagReport,
  type: ReportTypeId,
  context: ReportSatisfactionContext,
): boolean {
  if (!POWER_STRATEGY_REPORT_TYPES.has(type)) return true;

  const currentStrategy = (
    context.powerAvailabilityPreferences
    ?? context.site.typedPreferencesDefaults?.['power-availability']
  )?.btmStrategy;
  // An undeclared strategy is gated separately in Define. Do not invent a
  // strategy comparison while that decision is still unresolved.
  if (!currentStrategy) return true;

  const frozenStrategy = (
    report.typedPreferencesAtGen
    ?? report.typedPreferences
  )?.['power-availability']?.btmStrategy;

  // Fail closed for decision evidence: a report that cannot prove which
  // strategy it analysed remains readable, but cannot satisfy the live gate.
  return frozenStrategy === currentStrategy;
}

function reportTestFitAuthorityIsCurrent(
  report: SiteRagReport,
  context: ReportSatisfactionContext,
): boolean {
  if (!context.requiresGenerationImpact) return true;
  if (!context.testFitReadiness?.ready) return false;

  const promoted = context.site.promotedGenerationScenarioSnapshot;
  const head = context.site.generationImpactLayoutHead;
  const ref = report.testFitAuthorityRef;
  const frozenScenario = report.generationImpactScenarioAtGen;
  const frozenExhibit = report.generationLayoutExhibitAtGen;
  const projection = report.generationImpactProjection;
  const frozenImpact = report.generationImpactAtGen;

  // New on-site Test Fits are fail-closed: a legacy report without the compact
  // authority tuple remains readable, but cannot satisfy the decision gate.
  if (!promoted || !head || !ref || !frozenScenario || !frozenExhibit) return false;
  if (!projection?.testFitFootprint || !frozenImpact || !('noise' in frozenImpact)) {
    return false;
  }

  const impact = frozenImpact as GenerationImpactSnapshot;
  const frozenAuthority = frozenExhibit.provenance.frozenAuthority;
  const projectionSource = projection.source;
  if (
    !frozenAuthority
    || frozenAuthority.sedId == null
    || frozenAuthority.sedVersion == null
    || !validateGenerationLayoutExhibitSnapshot(frozenExhibit, impact).valid
  ) {
    return false;
  }

  // Only the collection identity may differ after a materially identical SED
  // rebuild. The original tuple still has to match its own immutable carriers.
  const currentAuthority = context.testFitReadiness.authority;
  const comparableAuthority = currentAuthority
    && reportSedMaterialDependenciesAreCurrent(report, context.site, context.sed, 'test-fit')
    ? { ...currentAuthority, sedId: ref.sedId, sedVersion: ref.sedVersion }
    : currentAuthority;

  return Boolean(
    sameTestFitAuthorityRef(ref, comparableAuthority)
      && frozenScenario.id === promoted.id
      && frozenScenario.updatedAt === promoted.updatedAt
      && frozenScenario.inputHash === promoted.inputHash
      && frozenScenario.resultHash === promoted.resultHash
      && impact.scenario.id === frozenScenario.id
      && impact.scenario.updatedAt === frozenScenario.updatedAt
      && impact.scenario.inputHash === frozenScenario.inputHash
      && impact.scenario.resultHash === frozenScenario.resultHash
      && frozenExhibit.scenario.id === promoted.id
      && frozenExhibit.scenario.updatedAt === promoted.updatedAt
      && frozenExhibit.scenario.inputHash === promoted.inputHash
      && frozenExhibit.scenario.resultHash === promoted.resultHash
      && frozenAuthority.scenarioId === ref.scenarioId
      && frozenAuthority.scenarioUpdatedAt === ref.scenarioUpdatedAt
      && frozenAuthority.inputHash === ref.inputHash
      && frozenAuthority.resultHash === ref.resultHash
      && frozenAuthority.modelVersion === ref.modelVersion
      && frozenAuthority.promotedAt === ref.promotedAt
      && frozenAuthority.layoutPublicationId === ref.layoutPublicationId
      && frozenAuthority.layoutVersion === ref.layoutVersion
      && frozenAuthority.layoutHash === ref.layoutHash
      && frozenAuthority.layoutPublishedAt === ref.layoutPublishedAt
      && frozenAuthority.sedId === ref.sedId
      && frozenAuthority.sedVersion === ref.sedVersion
      && frozenAuthority.boundaryHash === ref.boundaryHash
      && projectionSource.scenarioId === ref.scenarioId
      && projectionSource.inputHash === ref.inputHash
      && projectionSource.resultHash === ref.resultHash
      && projectionSource.modelVersion === ref.modelVersion
      && projectionSource.promotedAt === ref.promotedAt
      && projectionSource.layoutPublicationId === ref.layoutPublicationId
      && projectionSource.layoutVersion === ref.layoutVersion
      && projectionSource.layoutHash === ref.layoutHash
      && projectionSource.layoutPublishedAt === ref.layoutPublishedAt
      && projectionSource.parcelBoundaryHash === ref.boundaryHash,
  );
}

/**
 * Shared current-content predicate. Evidence must be finalized and
 * stamped against the current SED and Project Intent; unstamped legacy reports
 * remain readable but cannot satisfy the live gate. On-site Test Fit adds the
 * complete frozen Generation & Impact authority tuple and required carriers.
 */
function reportHasCurrentDecisionContent(
  report: SiteRagReport | null | undefined,
  type: ReportTypeId,
  context: ReportSatisfactionContext,
  useCanonicalInputCurrent = false,
): boolean {
  if (!report || (report.reportType ?? 'feasibility') !== type) return false;
  if (!reportPromptContractCurrency(report, type).current) return false;
  // Historical illustrative Financial reports remain readable, but their
  // baseline-derived economics are not project authority and can never satisfy
  // the live decision gate.
  if (
    type === 'financial'
    && Boolean(report.financialAnalysis?.illustrativeAssumptionsNote?.trim())
  ) {
    return false;
  }
  if (
    !evaluateReportSemanticReadiness(report).admittedForCurrentDecision
    || reportHasDeliveryHold(report, context.analysisGateEnabled === true)
    || !reportHasDecisionReadyRevision(
      report,
      context.analysisGateEnabled === true,
    )
    || report._metadataOnly
  ) return false;

  if (!useCanonicalInputCurrent && !reportSedIsCurrent(report, context.site, context.sed, type)) return false;
  if (!reportIntentIsCurrent(report, context.intent ?? context.site.projectIntent)) return false;
  if (!reportPowerStrategyIsCurrent(report, type, context)) return false;
  if (!reportGenerationImpactScenarioIsCurrent(report, type, context)) return false;
  if (type === 'test-fit' && !reportTestFitAuthorityIsCurrent(report, context)) return false;
  return true;
}

/** A completed scan is not positive evidence of community acceptance. */
function hasUnknownLocalStance(report: SiteRagReport | null | undefined, type: ReportTypeId): boolean {
  return type === 'public-sentiment' && (report?.screeningBasis?.sentimentScan?.outcome === 'coverage-only'
    || report?.screeningBasis?.sentimentScan?.localStanceBasis === 'no-dated-local-stance');
}

export function reportSatisfiesGo(
  report: SiteRagReport | null | undefined,
  type: ReportTypeId,
  context: ReportSatisfactionContext,
): boolean {
  return !hasUnknownLocalStance(report, type) && reportHasCurrentDecisionContent(report, type, context);
}

/**
 * Types whose validated screening run is a FINISHED task even without a
 * positive decision signal: every Stage 1 analysis, and Public Sentiment.
 *
 * Public Sentiment is an additional analysis outside both stages since the
 * owner's 2026-09-30 move, but its validated coverage-only /
 * no-dated-local-stance scan is still a completed, current run — not
 * "preliminary" refresh work. Reading stage membership here instead flipped
 * every such scan to `preliminary`, which the portfolio screening sweep
 * classifies `failed-or-held` and re-runs on every inventory without ever
 * converging (a re-run that again finds no dated local stance is again
 * preliminary).
 *
 * LIFECYCLE ONLY: `planItemCompletesStage` still reads Stage 1 membership, so
 * the scan completes no stage, satisfies no GO and scores no social pillar.
 */
function completesScreeningTask(type: ReportTypeId): boolean {
  return reportTypeStage(type) === 1 || type === 'public-sentiment';
}

/** Requires the full immutable body receipt and every normal currency/quality gate.
 * This completes the screening task, never a social score or a positive decision. */
export function reportCompletesScreening(
  report: SiteRagReport | null | undefined,
  type: ReportTypeId,
  context: ReportSatisfactionContext,
): boolean {
  return completesScreeningTask(type) && (reportSatisfiesGo(report, type, context)
    || Boolean(report && hasUnknownLocalStance(report, type)
      && hasValidatedDeterministicScreening(report)
      && reportHasCurrentDecisionContent(report, type, context)));
}

/** Shared producer/consumer admission for the exact accepted immutable
 * source body. Canonical proof substitutes only its SED comparison. */
export function reportIsCurrentCustomBriefSource(input: {
  report: SiteRagReport;
  type: ReportTypeId;
  context: ReportSatisfactionContext;
  planItem: ReportPlanItem;
  currentAnalysisInput?: DecisionAnalysisInputEnvelopeV1;
}): boolean {
  const { report, type, context, planItem, currentAnalysisInput } = input;
  if (planItem.type !== type || type === 'custom-brief' || type === 'executive-summary'
    || planItem.calculationStatus !== 'current' || planItem.stale
    || planItem.calculationReportId !== report.id || report.siteId !== context.site.id
    || !customBriefReportHasSourceBody(report) || !customBriefSourceHasImmutableRevision(report)
    || !customBriefSourceMatchesCurrentPlan(report, planItem, context.analysisGateEnabled)) return false;
  const canonicalCurrent = context.analysisGateEnabled === true
    && planItem.analysisReferenceValid === true && planItem.analysisReadiness?.ready === true
    && reportHasCanonicalCurrentSedInputs(report, type, currentAnalysisInput);
  // A present material stamp must belong to the admitted immutable semantic
  // input. Its independent match to the live SED cannot excuse a missing or
  // differently scoped stamp on that source snapshot.
  if (context.analysisGateEnabled === true
    && report.sedMaterialDependenciesAtGen !== undefined && !canonicalCurrent) return false;
  if (!canonicalCurrent) {
    return reportSatisfiesGo(report, type, context) || reportCompletesScreening(report, type, context);
  }
  return reportHasCurrentDecisionContent(report, type, context, true)
    && (!hasUnknownLocalStance(report, type)
      || (completesScreeningTask(type) && hasValidatedDeterministicScreening(report)));
}

/** Workflow completion is separate from the positive-decision gate. */
export function planItemCompletesStage(item: ReportPlanItem): boolean {
  if (item.contributesToCalculations === true) return true;
  if (
    reportTypeStage(item.type) === 1
    && item.screeningComplete === true
    && item.calculationStatus !== 'none'
  ) return true;
  // Backward compatibility for persisted plan snapshots that predate the
  // explicit calculation fields.
  return item.calculationStatus == null && (
    item.satisfiesGo || (reportTypeStage(item.type) === 1
      && item.screeningComplete === true && item.lifecycle === 'current' && !item.stale)
  );
}

/**
 * P3.5 (2026-09-05) — report-level scenario staleness for EVERY report type
 * that freezes the promoted generation scenario, not only Test Fit.
 *
 * The analysis path already handled this (a promoted-snapshot diff emits
 * `simulation-authority`, and the dependants include water-resource); the
 * report path compared the frozen scenario only for `test-fit`, so a Water
 * Resource or Generation & Impact report frozen against scenario A kept
 * satisfying GO after scenario B was promoted. Promoted scenarios are
 * immutable (saves targeting the promoted id throw and the client forks), so
 * "the scenario changed" is always "a different scenario was promoted" and an
 * id + hash comparison is sufficient — read from the typed home of the frozen
 * identity, `report.generationImpactProjection.source`, never from Firestore
 * metadata fields the report type does not declare.
 *
 * STRICT on the missing stamp, decided explicitly: when the site HAS a
 * promoted scenario and the report carries no frozen projection, the report
 * predates the promotion (or was generated without it) and its numbers do not
 * reflect the authority the decision now rests on — it does not satisfy GO.
 * The invalidation reaper regenerates such reports on promotion anyway; this
 * makes the gate agree with it. A site with no promoted scenario has nothing
 * to be stale against, so every report passes this check.
 */
function reportGenerationImpactScenarioIsCurrent(
  report: SiteRagReport,
  type: ReportTypeId,
  context: ReportSatisfactionContext,
): boolean {
  // Test Fit keeps its own, stricter authority tuple check (below).
  if (type === 'test-fit' || !isGenerationImpactReportType(type)) return true;
  const promoted = context.site.promotedGenerationScenarioSnapshot;
  if (!promoted) return true;
  const source = report.generationImpactProjection?.source;
  if (!source) return false;
  return source.scenarioId === promoted.id
    && source.inputHash === promoted.inputHash
    && source.resultHash === promoted.resultHash;
}

/**
 * Analysis-first alias for new decision surfaces. The legacy
 * `reportSatisfiesGo` export remains available for compatibility.
 */
export function analysisSatisfiesGo(
  analysis: SiteRagReport | null | undefined,
  type: ReportTypeId,
  context: AnalysisSatisfactionContext,
): boolean {
  return reportSatisfiesGo(analysis, type, context);
}

/** Exact owning-surface action for every stable Test Fit workflow state. */
export function pathActionForTestFitWorkflowCode(
  code: TestFitWorkflowCode,
  canEdit: boolean,
): PathAction {
  if (!canEdit) {
    const destination = code === 'ready'
      ? 'reports'
      : code.startsWith('sed-')
        ? 'sed'
        : 'generation-impact';
    return { kind: 'request-access', label: 'Request edit access', destination };
  }
  switch (code) {
    case 'feature-unavailable':
      return {
        kind: 'request-access',
        label: 'Request Generation & Impact access',
        destination: 'generation-impact',
      };
    case 'scenario-required':
      return {
        kind: 'generation-impact',
        label: 'Configure generation scenario',
        focus: 'scenario',
      };
    case 'save-required':
      return {
        kind: 'generation-impact',
        label: 'Save scenario',
        focus: 'save',
      };
    case 'scenario-conflict':
      // In the PERSISTED evaluator this code means the declared power intent
      // contradicts itself (grid-only strategy beside an on-site phase, and so
      // on) — it is checked before every other link and can only be cleared in
      // Project Intent. It was previously mapped to the Generation & Impact
      // handoff with the label "Reload saved scenario", which is the WORKBENCH
      // meaning of the same code (remote drift). That sent the user to a panel
      // which renders no control for this state, and no amount of saving,
      // promoting or publishing there could clear it. The ranked intent step
      // carries the specific conflicting field; this action only has to land
      // the reader on the surface that owns it.
      return {
        kind: 'intent',
        label: 'Resolve power intent',
      };
    case 'promotion-required':
      return {
        kind: 'generation-impact',
        label: 'Promote scenario',
        focus: 'promotion',
      };
    case 'publication-required':
      return {
        kind: 'generation-impact',
        label: 'Publish layout',
        focus: 'layout',
      };
    case 'sed-rebuilding':
      return { kind: 'sed-rebuild', label: 'View rebuild status', mode: 'status' };
    case 'sed-failed':
      return { kind: 'sed-rebuild', label: 'Retry SED rebuild', mode: 'retry' };
    case 'sed-rebuild-required':
      return { kind: 'sed-rebuild', label: 'Rebuild SED', mode: 'start' };
    case 'ready':
      return { kind: 'report-setup', label: 'Run Test Fit analysis', reportType: 'test-fit' };
  }
}

const TEST_FIT_CHAIN: ReadonlyArray<Pick<ReportDependencyStep, 'id' | 'label'>> = [
  { id: 'configure', label: 'Configure' },
  { id: 'save', label: 'Save' },
  { id: 'promote', label: 'Promote' },
  { id: 'publish', label: 'Publish layout' },
  { id: 'sed', label: 'Rebuild SED' },
  { id: 'test-fit', label: 'Test Fit' },
];

const CURRENT_CHAIN_INDEX: Record<TestFitWorkflowCode, number> = {
  'feature-unavailable': 0,
  'scenario-required': 0,
  'save-required': 1,
  'scenario-conflict': 1,
  'promotion-required': 2,
  'publication-required': 3,
  'sed-rebuild-required': 4,
  'sed-rebuilding': 4,
  'sed-failed': 4,
  ready: 5,
};

export function buildTestFitDependencyChain(
  readiness: TestFitWorkflowReadiness,
  testFitCurrent: boolean,
): ReportDependencyStep[] {
  const currentIndex = CURRENT_CHAIN_INDEX[readiness.code];
  return TEST_FIT_CHAIN.map((step, index) => {
    if (index < currentIndex) return { ...step, status: 'complete' };
    if (index > currentIndex) return { ...step, status: 'pending' };
    if (step.id === 'test-fit' && testFitCurrent) return { ...step, status: 'complete' };
    if (readiness.code === 'feature-unavailable' || readiness.code === 'sed-failed') {
      return { ...step, status: 'blocked' };
    }
    return { ...step, status: 'current' };
  });
}

export interface BuildReportPlanInput {
  site: SiteEntity;
  sed: SiteEvidenceDocument | null;
  reports: readonly SiteRagReport[];
  /** Immutable semantic analyses, each paired with its persisted locator. */
  analysisSnapshots?: readonly PathToGoAnalysisSnapshot[];
  /** Current canonical input identities, built by the owning analysis service. */
  currentAnalysisInputsByDomain?: Partial<
    Record<DecisionAnalysisDomain, DecisionAnalysisInputEnvelopeV1>
  >;
  intent: ProjectIntent | undefined;
  powerAvailabilityPreferences?: PowerAvailabilityPreferences;
  requiredTypes: readonly ReportTypeId[];
  scopeTypes: readonly ReportTypeId[];
  preflightByType: ReadonlyMap<
    ReportTypeId,
    {
      runnable: boolean;
      blockingSections: number;
      /** Exact missing SED section ids used by the Resolve inputs deep link. */
      blockingSectionIds?: readonly string[];
    }
  >;
  runtimeByType?: Partial<Record<ReportTypeId, ReportRuntimeLifecycle>>;
  outcomes?: readonly import('./reportRuntime').ReportRuntimeOutcome[];
  /** Domains whose pointer carries `autoRebindExhaustedAt`. */
  autoRefreshExhaustedByType?: Partial<Record<ReportTypeId, boolean>>;
  /** Only gate mode may use semantic readiness for positive decisions. */
  analysisGateEnabled?: boolean;
  generationTabEnabled?: boolean;
  canEdit?: boolean;
  requiresGenerationImpact: boolean;
  /**
   * Include every generated decision product after the required and
   * intent-relevant products. `buildAnalysisPlan` enables this; the legacy
   * `buildReportPlan` entry point keeps its historical scoped behavior.
   */
  includeFullGeneratedCatalog?: boolean;
}

function getDeliveryHoldClassification(
  reportInput: SiteRagReport,
  type: ReportTypeId,
  canEdit: boolean,
  analysisGateEnabled: boolean,
): { lifecycle: ReportLifecycle; blockingReason: string; primaryAction: PathAction } | null {
  const report = reportForDecisionEligibility(reportInput);
  const hasHold = reportHasDeliveryHold(report, analysisGateEnabled);
  if (!hasHold) return null;

  const revision = report.reportRevision;
  const decision = revision?.deliveryDecision;
  const useSemanticRevision = Boolean(
    revision
      && (analysisGateEnabled || hasIndependentReportContent(report.pdfDelivery, revision.deliveryDecision))
      && reportHasSemanticAnalysisRevision(report),
  );
  // Extract non-advisory reason codes (ignoring presentation-only PDF/layout blocks on semantic revisions)
  const nonAdvisoryReasons = decision && Array.isArray(decision.reasons)
    ? decision.reasons.filter(r => (
        !isAuditAdvisoryDeliveryReason(r)
        && (!useSemanticRevision || !PRESENTATION_ONLY_DELIVERY_BLOCKS.has(r.code))
      ))
    : [];

  // Find first code
  let code: string | undefined = nonAdvisoryReasons[0]?.code;
  let message: string | undefined = nonAdvisoryReasons[0]?.message;

  if (!code) {
    const semanticHold = evaluateReportSemanticReadiness(report).holdReason;
    if (semanticHold) {
      code = semanticHold.code;
      message = semanticHold.message;
    } else if (report.generationTier === 'draft' || (report.isDraft && report.qualityHold !== 'needs-revision')) {
      code = 'draft';
      message = 'Draft-tier reports are private review artifacts and cannot be delivered.';
    } else if (report.degradedMode) {
      code = 'degraded';
      message = report.degradedReason?.trim()
        ? `The report uses degraded content: ${report.degradedReason}`
        : 'The report uses degraded content and cannot be exported or published.';
    } else if (report.requiresRegeneration) {
      code = 'retired-evidence';
      message = 'The report requires regeneration due to evidence updates.';
    }
  }

  if (code === 'unauthorized' || code === 'expired') {
    return {
      lifecycle: 'dependency-blocked',
      blockingReason: message ?? 'This analysis requires updated access authority.',
      primaryAction: {
        kind: 'request-access',
        label: 'Request access',
        destination: 'reports',
      },
    };
  }

  if (code === 'body-unavailable' || code === 'metadata-only') {
    return {
      lifecycle: 'failed',
      blockingReason: message ?? 'The immutable report body could not be loaded.',
      primaryAction: canEdit
        ? { kind: 'report-setup', label: 'Reload report', reportType: type }
        : { kind: 'request-access', label: 'Request edit access', destination: 'reports' },
    };
  }

  if (code === 'retired-evidence' || code === 'requires-regeneration') {
    return {
      lifecycle: 'refresh-needed',
      blockingReason: message ?? 'The report depends on retired evidence and must be regenerated.',
      primaryAction: canEdit
        ? { kind: 'report-setup', label: 'Regenerate report', reportType: type }
        : { kind: 'request-access', label: 'Request edit access', destination: 'reports' },
    };
  }

  if (code === 'degraded' || code === 'schema-invalid') {
    return {
      lifecycle: 'failed',
      blockingReason: message ?? 'The report revision is a degraded, view-only artifact.',
      primaryAction: canEdit
        ? { kind: 'report-setup', label: 'Retry analysis', reportType: type }
        : { kind: 'request-access', label: 'Request edit access', destination: 'reports' },
    };
  }

  if (code === 'draft') {
    return {
      lifecycle: 'preview',
      blockingReason: message ?? 'Draft-tier reports are private review artifacts.',
      primaryAction: canEdit
        ? { kind: 'report-setup', label: 'Generate written report', reportType: type }
        : { kind: 'request-access', label: 'Request edit access', destination: 'reports' },
    };
  }

  // Fallback to quality-held
  return {
    lifecycle: 'quality-held',
    blockingReason: 'This analysis must pass quality review before it can support a decision.',
    primaryAction: {
      kind: 'report-qa',
      label: 'Review quality hold',
      reportType: type,
      reportId: report.id,
    },
  };
}

export function buildReportPlan(input: BuildReportPlanInput): ReportPlanItem[] {
  const requestedTypes = [...new Set<ReportTypeId>([
    ...input.requiredTypes,
    ...input.scopeTypes,
    ...(input.includeFullGeneratedCatalog
      ? GENERATED_DECISION_PRODUCT_IDS
      : []),
  ])];
  // Evaluate sibling source rows once in the same plan, even when a caller
  // requests only Custom Brief. These dependency rows stay out of its result;
  // the final pass uses their normal accepted/current calculation authority.
  const tracksBriefSources = requestedTypes.includes('custom-brief')
    && input.reports.some(report => report.siteId === input.site.id && report.reportType === 'custom-brief'
      && customBriefSourceFrame(report) !== null);
  const dependencyTypes = tracksBriefSources ? input.reports
    .filter(report => report.siteId === input.site.id && report.reportType !== 'custom-brief' && isEligibleExecutiveSummarySource(report))
    .map(report => report.reportType!) : [];
  const allTypes = [...new Set([...requestedTypes, ...dependencyTypes])]
    .filter(type => {
      const product = getDecisionProduct(type);
      return Boolean(product)
        && (!input.includeFullGeneratedCatalog || product.generation === 'generated')
        && product.generation !== 'retired';
    });
  const canEdit = input.canEdit !== false;
  const testFitReadiness = evaluatePersistedTestFitWorkflow(input.site, input.sed, {
    ...(input.generationTabEnabled != null
      ? { generationImpactEnabled: input.generationTabEnabled }
      : {}),
    requireGenerationImpact: input.requiresGenerationImpact,
  });
  const promotedScenario = input.site.promotedGenerationScenarioSnapshot;
  const layoutHead = input.site.generationImpactLayoutHead;
  const testFitAuthorityRevision: NonNullable<ReportAuthorityState['revision']> = {
    ...(promotedScenario
      ? {
          scenarioId: promotedScenario.id,
          scenarioName: promotedScenario.name,
          scenarioUpdatedAt: promotedScenario.updatedAt,
        }
      : {}),
    ...(layoutHead
      ? {
          layoutPublicationId: layoutHead.publicationId,
          layoutVersion: layoutHead.version,
        }
      : {}),
    ...(input.site.sedId ? { sedId: input.site.sedId } : {}),
    ...(input.site.sedVersion != null ? { sedVersion: input.site.sedVersion } : {}),
  };

  const plan = allTypes.map((type): ReportPlanItem => {
    const applicability = resolveAnalysisApplicability({
      type,
      site: input.site,
      requiredTypes: input.requiredTypes,
      scopeTypes: input.scopeTypes,
      // The SAME intent the required-set calculation used. Path-to-GO threads
      // intent separately from the site record, so reading `site.projectIntent`
      // here would let the two disagree — one engine calling Power required
      // while the other files it as later work for the same site.
      intent: input.intent,
    });
    const role: ReportPlanRole = applicability.role;
    const product = getDecisionProduct(type);
    const authorityReadiness = evaluateReportAuthorityReadiness(type, input.site);
    // TWO-STAGE (Phase 1 follow-through, 2026-09-06) — Generation & Impact is
    // gated on a PROMOTED scenario (Stage 5.2). Until now the plan gave a
    // scenario-less Generation & Impact a 'ready' lifecycle and a "Run
    // analysis" action that the server then refused; Path to GO and the
    // Analyses list showed a button that could not work. The plan now says
    // what Test Fit says: configure and promote a scenario first.
    const generationReadiness = type === 'power-availability'
      ? evaluateGenerationImpactReportReadiness({
          projectIntent: input.intent,
          promotedGenerationScenarioSnapshot: input.site.promotedGenerationScenarioSnapshot,
          typedPreferencesDefaults: input.site.typedPreferencesDefaults,
        })
      : null;
    const generationScenarioBlocked = Boolean(
      generationReadiness
      && !generationReadiness.ready
      && ['generation-scenario-missing', 'generation-scenario-stale'].includes(generationReadiness.blockers[0]?.code ?? ''),
    );
    const usesDedicatedAuthority = (
      product.prerequisite === 'financial-model'
      || product.prerequisite === 'equipment-bom'
    );
    // `report` remains the newest lifecycle/display row. Calculations select
    // independently so a partial or failed retry cannot erase a prior success.
    const report = latestReportOfType(input.reports, type);
    const calculationReport = latestUsableSavedReportOfType(input.reports, type);
    const analysis = latestAnalysisOfType(input.analysisSnapshots ?? [], type);
    const domain = decisionAnalysisDomain(type);
    const currentAnalysisInput = domain
      ? input.currentAnalysisInputsByDomain?.[domain]
      : undefined;
    const evaluatedAnalysisReadiness = analysis
      ? evaluateDecisionAnalysisReadiness(
          analysis.snapshot,
          currentAnalysisInput,
          // Keep the pure Path-to-GO projection byte-stable for identical
          // inputs; wall-clock evaluation time is not decision evidence.
          { evaluatedAt: analysis.revision.createdAt },
        )
      : undefined;
    /*
     * The server-owned current pointer can carry an operational stale/held/
     * failed state while its immutable snapshot remains internally valid.
     * Re-evaluating that snapshot against its own frozen input must never
     * upgrade the pointer back to ready. Either source may make readiness more
     * restrictive; neither may make the other more permissive.
     */
    const persistedReadiness = analysis?.revision?.readiness;
    const analysisReadiness = analysis && evaluatedAnalysisReadiness
      ? (
          !persistedReadiness
            ? evaluatedAnalysisReadiness
          : analysis.judgedLatestAttempt === true && !persistedReadiness.ready
            /*
             * A held/degraded LATEST ATTEMPT is judged on its own input, the
             * basis the invalidation reaper uses (latestAttemptJudgedOnInput),
             * so "Refresh required" and the reaper's enqueue agree domain by
             * domain. Drift in anything it binds reads stale
             * ('refresh-needed'); otherwise the attempt's own committed
             * held/degraded readiness and reason stand. Both are non-ready:
             * the pointer was never advanced to this attempt, so it is not
             * decision-usable, and the audit-advisory deferral below (which
             * can re-derive 'ready') does not apply to it.
             */
            ? (
                evaluatedAnalysisReadiness.state === 'stale'
                  ? evaluatedAnalysisReadiness
                  : persistedReadiness
              )
          : persistedReadiness.ready
            ? evaluatedAnalysisReadiness
            // AUDIT-ADVISORY (2026-08-22): a persisted pointer whose ONLY
            // non-ready reasons are audit outcomes ('quality-held' / the
            // semantic 'hard-gate-failed') defers to the live re-evaluation —
            // which re-derives staleness, adequacy and hash integrity from
            // the frozen snapshot, so nothing else becomes more permissive.
            : (persistedReadiness.reasons ?? []).length > 0
                && (persistedReadiness.reasons ?? []).every(
                  (row) => row.code === 'quality-held' || row.code === 'hard-gate-failed',
                )
                // ... and the frozen snapshot itself is audit-held. An
                // OPERATIONAL 'quality-held' overlay (e.g. a data-inadequate
                // rerun whose attempt failed to load) sits over a PASSING
                // snapshot — deferring there would upgrade past a
                // fail-closed overlay that is not an audit stamp.
                && analysis.snapshot?.semantic?.quality?.state === 'held'
              ? evaluatedAnalysisReadiness
              : persistedReadiness
        )
      : undefined;
    const analysisReferenceValid = analysis
      ? analysisReferenceMatchesSnapshot(analysis)
      : false;
    // The automatic-rebind gate's verdict on the SAME judged analysis and live
    // input the reaper uses: stale, but only on optional currency evidence.
    const optionalEvidenceOnly = Boolean(
      analysis?.snapshot?.input
      && currentAnalysisInput
      && analysisReadiness?.state === 'stale'
      && evaluatedAnalysisReadiness?.state === 'stale'
      && classifyAutoRebindDrift(type, decisionAnalysisInputDrift(analysis.snapshot.input, currentAnalysisInput))
        === 'optional-evidence-only',
    );
    const selectedSimulationAuthority =
      input.site.engineeringSourceBasis?.kind === 'simulation-augmented'
        ? input.site.engineeringSourceBasis.authority
        : null;
    const analysisReadinessMessage = analysisReadiness?.reason?.message ?? '';
    const simulationAuthorityNeedsResolution = Boolean(
      selectedSimulationAuthority
      && !analysisReadiness?.ready
      && /(simulation|engineering source|eo run|solv(?:e|ed))/i.test(
        analysisReadinessMessage,
      ),
    );
    const engineeringSourceAction: PathAction = {
      kind: 'engineering-source',
      label: 'Resolve inputs',
      ...(selectedSimulationAuthority
        ? {
            simulationId: selectedSimulationAuthority.simulationId,
            eoRunId: selectedSimulationAuthority.eoRunId,
          }
        : {}),
    };
    const context: ReportSatisfactionContext = {
      site: input.site,
      sed: input.sed,
      intent: input.intent,
      powerAvailabilityPreferences: input.powerAvailabilityPreferences,
      requiresGenerationImpact: input.requiresGenerationImpact,
      analysisGateEnabled: input.analysisGateEnabled === true,
      ...(type === 'test-fit' ? { testFitReadiness } : {}),
    };
    const coverageRefusal = enqueueCoverageRefusalFor(type, input.sed, {
      dispatchWaivers: input.site.siteConfigOverrides?.providerCoverageDispatchWaivers,
      auditOverrides: input.site.auditOverrides,
    });
    const outcome = input.outcomes
      ? [...input.outcomes]
          .filter(o => o.siteId === input.site.id && o.reportTypeId === type)
          .sort((a, b) => Date.parse(b.finishedAt) - Date.parse(a.finishedAt))[0]
      : undefined;
    const savedCoverageOnlyOutcome = hasUnknownLocalStance(calculationReport, type);
    const analysisCoverageOnlyOutcome = type === 'public-sentiment'
      && analysis?.snapshot?.semantic?.narrative?.state === 'ready'
      && analysis.snapshot.semantic.narrative.generationMethod === 'deterministic-source-screening';
    const coverageOnlyOutcome = input.analysisGateEnabled === true && analysis
      ? analysisCoverageOnlyOutcome
      : savedCoverageOnlyOutcome;
    const reportReady = reportSatisfiesGo(calculationReport, type, context);
    const hasIncompleteReportAnalysis = Boolean(report && reportHasIncompleteDecisionAnalysis(report, type));
    const analysisReady = Boolean(
      analysisReadiness?.ready && analysisReferenceValid && !hasIncompleteReportAnalysis,
    );
    const currentReportCompletesScreening = reportCompletesScreening(
      calculationReport,
      type,
      context,
    );
    const savedReportCompletesScreening = Boolean(
      calculationReport
      && completesScreeningTask(type)
      && (!savedCoverageOnlyOutcome
        || hasValidatedDeterministicScreening(calculationReport)),
    );
    const screeningComplete = savedReportCompletesScreening;
    const currentWorkflowReport = reportReady || currentReportCompletesScreening;
    const analysisSatisfiesPositiveGate = Boolean(
      input.analysisGateEnabled === true && analysisReady && !coverageOnlyOutcome,
    );
    // `satisfiesGo` retains the strict exact-current semantic signal for
    // lifecycle/audit presentation. Calculation usability is projected
    // separately below: a safe saved report may remain the disclosed fallback
    // while that pointer is stale, refreshing, or failed. Withheld narrative
    // still cannot become current authority.
    const hasWithheldNarrative = Boolean(report && reportHasWithheldNarrative(report));
    const satisfiesGo = input.analysisGateEnabled === true && domain !== null
      ? analysisReady && !coverageOnlyOutcome && !hasWithheldNarrative
      : reportReady;
    const contributesToCalculations = Boolean(
      (analysisSatisfiesPositiveGate && !hasWithheldNarrative)
      || (calculationReport && !savedCoverageOnlyOutcome),
    );
    const calculationIsCurrent = input.analysisGateEnabled === true && domain !== null
      ? (
          (analysisSatisfiesPositiveGate && !hasWithheldNarrative)
          || (analysisReady && coverageOnlyOutcome && savedCoverageOnlyOutcome)
        )
      : currentWorkflowReport;
    const calculationStatus: ReportCalculationStatus = calculationIsCurrent
      ? 'current'
      : calculationReport
        ? 'saved'
        : 'none';
    const reportCanonicalSedCurrent = Boolean(input.analysisGateEnabled === true
      && analysisReady && analysis && report?.analysisRevision
      && analysisReferenceMatchesSnapshot({ revision: report.analysisRevision, snapshot: analysis.snapshot })
      && reportHasCanonicalCurrentSedInputs(report, type, currentAnalysisInput));
    const reportStale = Boolean(
      report
      && (
        !reportPromptContractCurrency(report, type).current
        || evaluateReportSemanticReadiness(report).stale
        || (!reportCanonicalSedCurrent && !reportSedIsCurrent(report, input.site, input.sed, type))
        || !reportIntentIsCurrent(report, input.intent)
        || !reportPowerStrategyIsCurrent(report, type, context)
        || !reportHasDecisionReadyRevision(
          report,
          input.analysisGateEnabled === true,
        )
        || (
          type === 'test-fit'
          && !reportTestFitAuthorityIsCurrent(report, context)
        )
      ),
    );
    const stale = !satisfiesGo && Boolean(
      analysisReadiness?.state === 'stale' || reportStale,
    );
    const runtime = input.runtimeByType?.[type];
    const preflight = input.preflightByType.get(type) ?? {
      runnable: false,
      blockingSections: 0,
    };
    // Generic SED coverage is a confidence/limitations input, not a launch
    // gate. True calculation prerequisites remain explicit below (financial
    // model, equipment BoM, Custom Brief definition, and Test Fit authority).
    // This lets a screening analysis run from the evidence available today and
    // be refreshed when additional sources arrive.
    const sourceImprovementCount = preflight.blockingSections;
    const testFitBlocked =
      type === 'test-fit'
      && input.requiresGenerationImpact
      && !testFitReadiness.ready;
    const customBrief = type === 'custom-brief'
      ? customBriefPreferencesFromSite(input.site)
      : undefined;
    const currentBriefHasDefinition = type === 'custom-brief'
      && Boolean(calculationReport?.reportContentRevision)
      && Boolean(calculationReport && customBriefContentMatchesCurrentInputs(calculationReport, input.site, input.sed));
    const customBriefReady = type !== 'custom-brief' || currentBriefHasDefinition || Boolean(
      customBrief
      && typeof customBrief.briefName === 'string'
      && customBrief.briefName.trim()
      && Array.isArray(customBrief.questions)
      && customBrief.questions.some(question => (
        typeof question?.id === 'string'
        && question.id.trim()
        && typeof question.text === 'string'
        && question.text.trim()
      )),
    );

    let lifecycle: ReportLifecycle;
    let primaryAction: PathAction;
    let blockingReason: string | undefined;
    let savedWithoutCurrentAnalysis = false;
    let authority: ReportAuthorityState = {
      kind: 'general',
      status: 'not-applicable',
      detail: 'No additional publishing authority is required.',
    };
    if (type === 'test-fit' && input.requiresGenerationImpact && testFitReadiness.ready) {
      authority = {
        kind: 'generation-impact',
        status: 'valid',
        detail: testFitReadiness.message,
        workflowCode: testFitReadiness.code,
        revision: testFitAuthorityRevision,
      };
    }

    if (!customBriefReady) {
      lifecycle = 'needs-input';
      blockingReason =
        'Name the Custom Brief and add at least one diligence question before running it.';
      primaryAction = canEdit
        ? {
            kind: 'report-setup',
            label: 'Resolve inputs',
            reportType: 'custom-brief',
          }
        : {
            kind: 'request-access',
            label: 'Request edit access',
            destination: 'reports',
          };
      authority = {
        kind: 'general',
        status: 'missing',
        detail: blockingReason,
      };
    } else if (usesDedicatedAuthority && !authorityReadiness.ready) {
      lifecycle = 'needs-input';
      blockingReason = formatReportAuthorityBlockers(authorityReadiness);
      primaryAction = canEdit
        ? type === 'financial'
          ? {
              kind: 'financial-inputs',
              label: 'Resolve inputs',
            }
          : {
              kind: 'bom',
              label: 'Resolve inputs',
            }
        : {
            kind: 'request-access',
            label: 'Request edit access',
            destination: 'reports',
          };
      authority = {
        kind: 'general',
        status: 'missing',
        detail: blockingReason,
      };
    } else if (generationScenarioBlocked && generationReadiness) {
      lifecycle = 'needs-input';
      blockingReason = generationReadiness.blockers[0]?.message
        ?? 'Promote a solved generation scenario before running Generation & Impact analysis.';
      primaryAction = canEdit
        ? { kind: 'generation-impact', label: 'Configure generation scenario', focus: 'scenario' }
        : { kind: 'request-access', label: 'Request Generation & Impact access', destination: 'generation-impact' };
      authority = {
        kind: 'generation-impact',
        status: 'missing',
        detail: blockingReason,
      };
    } else if (testFitBlocked) {
      lifecycle = testFitReadiness.code === 'feature-unavailable'
        ? 'unavailable'
        : 'dependency-blocked';
      blockingReason = testFitReadiness.message;
      primaryAction = pathActionForTestFitWorkflowCode(testFitReadiness.code, canEdit);
      authority = {
        kind: 'generation-impact',
        status: testFitReadiness.code === 'feature-unavailable' ? 'unavailable' : 'missing',
        detail: testFitReadiness.message,
        workflowCode: testFitReadiness.code,
        revision: testFitAuthorityRevision,
      };
    } else if (runtime === 'queued' || runtime === 'generating') {
      lifecycle = runtime;
      primaryAction = { kind: 'report-progress', label: 'View analysis progress', reportType: type };
    } else if (runtime === 'failed-coverage') {
      lifecycle = 'failed-coverage';
      blockingReason = 'Analysis was blocked by missing provider coverage. Refresh evidence or request a waiver.';
      primaryAction = canEdit
        ? { kind: 'report-setup', label: 'Refresh evidence / request waiver', reportType: type }
        : {
            kind: 'request-access',
            label: 'Request edit access',
            destination: 'reports',
          };
    } else if (runtime === 'failed') {
      // Refresh lifecycle remains visible even while a previous successful
      // revision continues to supply calculations.
      lifecycle = 'failed';
      blockingReason = 'Analysis generation failed due to a compilation or provider fault. Retry the calculation.';
      primaryAction = canEdit
        ? simulationAuthorityNeedsResolution
          ? engineeringSourceAction
          : { kind: 'report-setup', label: 'Retry analysis', reportType: type }
        : {
            kind: 'request-access',
            label: 'Request edit access',
            destination: 'reports',
          };
    } else if (coverageRefusal && !currentWorkflowReport) {
      lifecycle = 'failed-coverage';
      blockingReason = coverageRefusal.message;
      primaryAction = canEdit
        ? { kind: 'report-setup', label: 'Refresh evidence / request waiver', reportType: type }
        : {
            kind: 'request-access',
            label: 'Request edit access',
            destination: 'reports',
          };
    } else if (
      analysisReady
      && analysis
      && input.analysisGateEnabled === true
      && (!coverageOnlyOutcome || screeningComplete)
      && !hasWithheldNarrative
    ) {
      lifecycle = 'current';
      primaryAction = {
        kind: 'report-qa',
        label: 'Review analysis',
        reportType: type,
      };
    } else if (
      report
      && currentWorkflowReport
      && (input.analysisGateEnabled !== true || type === 'custom-brief')
    ) {
      // Custom Brief has its own content lifecycle rather than a semantic
      // analysis domain. Its current written revision remains the carrier.
      // A printable-artifact failure does not invalidate current content.
      lifecycle = 'current';
      primaryAction = {
        kind: 'report-qa',
        label: 'Review analysis',
        reportType: type,
        reportId: report.id,
      };
    } else if (analysisReady && analysis && !report) {
      /*
       * In shadow/read mode the semantic analysis may be internally current,
       * but the ACTIVE positive-decision gate still belongs to the legacy
       * report. Calling that row "Current" while `satisfiesGo` is false made
       * Findings render the contradictory pair "Current" / "Not
       * decision-current". Keep the analysis readable, but name its actual
       * authority: it is a preview until gate mode is enabled.
       */
      lifecycle = 'preview';
      blockingReason =
        'An analysis preview is available. Generate the written report from the current site evidence before it can support a decision.';
      primaryAction = canEdit
        ? { kind: 'report-setup', label: 'Generate written report', reportType: type }
        : { kind: 'request-access', label: 'Request edit access', destination: 'reports' };
    } else if (analysis && analysisReadiness && !analysisReady) {
      blockingReason = !analysisReferenceValid
        ? 'The persisted analysis reference does not match its immutable semantic snapshot.'
        : hasIncompleteReportAnalysis
          ? 'The report decision-analysis record is incomplete and requires refresh.'
          : analysisReadiness.reason?.message
            ?? 'The semantic analysis is not ready for a final decision.';
      if (simulationAuthorityNeedsResolution) {
        lifecycle = 'quality-held';
        primaryAction = canEdit
          ? engineeringSourceAction
          : {
              kind: 'request-access',
              label: 'Request edit access',
              destination: 'reports',
            };
      } else if ((analysisReferenceValid && analysisReadiness.state === 'stale') || hasIncompleteReportAnalysis) {
        lifecycle = 'refresh-needed';
        primaryAction = canEdit
          ? { kind: 'report-setup', label: 'Refresh analysis', reportType: type }
          : {
              kind: 'request-access',
              label: 'Request edit access',
              destination: 'reports',
            };
      } else if (
        analysisReferenceValid
        && ['queued', 'refreshing'].includes(analysisReadiness.state)
      ) {
        lifecycle = 'preliminary';
        primaryAction = analysisReadiness.reason?.code === 'narrative-pending'
          ? { kind: 'report-progress', label: 'View analysis progress', reportType: type }
          : {
              kind: 'report-qa',
              label: 'Review preliminary analysis',
              reportType: type,
            };
      } else if (
        analysisReferenceValid
        && analysisReadiness.state === 'failed'
      ) {
        lifecycle = 'failed';
        primaryAction = canEdit
          ? { kind: 'report-setup', label: 'Retry analysis', reportType: type }
          : {
              kind: 'request-access',
              label: 'Request edit access',
              destination: 'reports',
            };
      } else {
        lifecycle = 'quality-held';
        primaryAction = {
          kind: 'report-qa',
          label: 'Review analysis hold',
          reportType: type,
        };
      }
    } else if (
      report
      && reportHasDeliveryHold(report, input.analysisGateEnabled === true)
    ) {
      const holdClassification = getDeliveryHoldClassification(report, type, canEdit, input.analysisGateEnabled === true);
      if (holdClassification) {
        lifecycle = holdClassification.lifecycle;
        primaryAction = holdClassification.primaryAction;
        blockingReason = holdClassification.blockingReason;
      } else {
        lifecycle = 'quality-held';
        primaryAction = {
          kind: 'report-qa',
          label: 'Review quality hold',
          reportType: type,
          reportId: report.id,
        };
        blockingReason = 'This analysis must pass quality review before it can support a decision.';
      }
    } else if (report && coverageOnlyOutcome && !reportStale) {
      lifecycle = 'preliminary';
      primaryAction = { kind: 'report-qa', label: 'Review coverage screening', reportType: type, reportId: report.id };
      blockingReason = 'The current source-coverage screening is complete. Community stance remains unknown; additional verified local evidence is needed for a positive decision.';
    } else if (report && !reportReady) {
      lifecycle = 'refresh-needed';
      primaryAction = canEdit
        ? { kind: 'report-setup', label: 'Refresh analysis', reportType: type }
        : {
            kind: 'request-access',
            label: 'Request edit access',
            destination: 'reports',
          };
      blockingReason = !reportPromptContractCurrency(report, type).current
        ? 'Refresh this report using the latest site evidence and screening criteria.'
        : type === 'test-fit'
          ? 'The analysis no longer matches the current Generation & Impact, layout, boundary, and SED authority.'
          : 'The analysis was computed from an earlier evidence revision.';
      authority = type === 'test-fit'
        ? {
            kind: 'generation-impact',
            status: 'stale',
            detail:
              'The analysis no longer matches the current Generation & Impact, layout, boundary, and SED authority.',
            workflowCode: testFitReadiness.code,
            revision: testFitAuthorityRevision,
          }
        : authority;
    } else if (report && domain) {
      /*
       * A SAVED report reaches this fall-through only in gate mode (legacy
       * mode returns every report above as current or refresh-needed), and
       * chiefly when the report passes the report checks but no current
       * semantic analysis is loaded for the row. Calling it
       * "Ready to analyse" with "Run analysis" told the reader the analysis
       * had never run, while Findings listed the saved report beside it
       * (2026-09-18). It is refresh work, in the existing vocabulary.
       *
       * ONLY for a type with a decision-analysis domain: a refresh produces
       * that type's analysis. A Custom Brief (or any presentation-only type)
       * has no domain, so gate mode can never call it current — "Refresh
       * analysis" there would invite a rerun that can never clear the label.
       * It keeps the final branch below, as before this change.
       *
       * PRESENTATION ONLY: `satisfiesGo`, `stale`, `screeningComplete` and
       * `decisionEvidenceSource` are computed above from the gate inputs and
       * do not read `lifecycle`; `planItemCompletesStage` already requires
       * `lifecycle === 'current'`, which neither label is. No `blockingReason`
       * is set on purpose: the New Report wizard renders any blocking reason
       * as "Source inputs not ready … the request will be refused", which a
       * refresh of a saved report is not. `savedWithoutCurrentAnalysis` tells
       * next-action to rank and word the row as the saved, unscored run it is.
       */
      lifecycle = 'refresh-needed';
      savedWithoutCurrentAnalysis = true;
      primaryAction = canEdit
        ? { kind: 'report-setup', label: 'Refresh analysis', reportType: type }
        : {
            kind: 'request-access',
            label: 'Request edit access',
            destination: 'reports',
          };
    } else {
      lifecycle = 'ready';
      primaryAction = canEdit
        ? {
            kind: 'report-setup',
            label: type === 'test-fit' ? 'Run Test Fit analysis' : 'Run analysis',
            reportType: type,
          }
        : {
            kind: 'request-access',
            label: 'Request edit access',
            destination: 'reports',
          };
    }

    /*
     * Every report-quality action must retain the carrier identity. Several
     * analysis-first branches (current, preview, held) are selected from a
     * semantic snapshot but still have a report available; dropping its id
     * made the Decision Cockpit open an unfiltered Audit screen.
     */
    if (primaryAction.kind === 'report-qa' && !primaryAction.reportId && report) {
      primaryAction = { ...primaryAction, reportId: report.id };
    }

    /*
     * FIN-OUTPUT-GAPS — an UNFINANCEABLE but complete financial model now
     * passes the authority gate (its undefined IRR / payback are engine
     * OUTPUTS, not missing inputs), so it never reaches the `needs-input` +
     * "Resolve inputs" branch above. Carry the exact reason codes onto the
     * plan item's authority detail instead, so the surface states WHY the
     * decision metrics are absent rather than leaving the reader to read a
     * genuine unfinanceable result as a data gap.
     */
    const financialDecisionMetricGaps =
      type === 'financial' && authorityReadiness.ready
        ? formatFinancialDecisionMetricGaps(authorityReadiness.financialDecisionMetrics)
        : null;
    if (financialDecisionMetricGaps && authority.status === 'not-applicable') {
      authority = {
        kind: 'general',
        status: 'valid',
        detail: financialDecisionMetricGaps,
      };
    }

    const dependencyChain = type === 'test-fit' && input.requiresGenerationImpact
      ? buildTestFitDependencyChain(testFitReadiness, lifecycle === 'current')
      : undefined;

    const auditRecommendation = report ? reportAuditRecommendation(report) : null;

    const dependencyUnavailable = (
      lifecycle === 'needs-input'
      || lifecycle === 'dependency-blocked'
      || lifecycle === 'unavailable'
    );
    // `available-later` means the analysis cannot be produced yet — either the
    // domain is not decision-material (Financial without a complete model) or
    // its own inputs do not exist.
    //
    // TEST FIT (2026-09-03): previously a required analysis whose next action
    // was setup stayed in run-now, on the reasoning that the setup gap itself
    // blocks the decision path. In practice that put an analysis the user is
    // physically unable to run at the TOP of the queue — Test Fit needs a
    // promoted generation scenario, a layout head and a current SED, and
    // without them it renders as priority #1 and then refuses to run. That is
    // the same "pushed at a report without its base data" problem the
    // Financial / Supply Chain gate exists to prevent, so it is now treated
    // the same way: moved to `available-later` with the prerequisite named.
    //
    // Its ROLE is unchanged — Test Fit still gates GO, so readiness continues
    // to reflect that the site cannot reach GO until it runs. It simply stops
    // presenting itself as the next runnable step.
    if (screeningComplete && coverageOnlyOutcome && lifecycle === 'current') {
      blockingReason = 'Screening complete. Community stance remains unknown; verified local evidence is still needed for a positive decision.';
    }
    const eligibilityGroup: AnalysisEligibilityGroup =
      testFitBlocked ? 'available-later' : applicability.eligibilityGroup;
    const eligibilityReasonCode: AnalysisApplicabilityReasonCode =
      testFitBlocked || (dependencyUnavailable && applicability.prerequisiteReady)
        ? 'dependency-not-ready'
        : applicability.reasonCode;
    const eligibilityReason =
      dependencyUnavailable
        ? blockingReason ?? applicability.reason
        : applicability.reason;

    return {
      type,
      role,
      lifecycle,
      prerequisites: type === 'test-fit' && input.requiresGenerationImpact
        ? ['generation-impact-scenario', 'generation-impact-layout', 'current-sed']
        : PREREQUISITE_TOKENS[product.prerequisite],
      authority,
      stale,
      satisfiesGo,
      calculationStatus,
      contributesToCalculations,
      ...(calculationReport ? { calculationReportId: calculationReport.id } : {}),
      ...(outcome?.errorCode ? { errorCode: outcome.errorCode } : {}),
      ...(outcome?.terminalReason ? { terminalReason: outcome.terminalReason } : {}),
      ...(outcome?.terminalReason === 'repair-deadline-reserved' && lifecycle === 'quality-held'
        ? {
            repairState: 'resumable' as const,
            blockingReason:
              'Automated repair pass was skipped because the job deadline reserve was reached while quality/grounding findings remained; repair is resumable from the saved candidate.',
          }
        : {}),
      ...(outcome?.blockedByProviderCoverage || outcome?.errorCode === 'PROVIDER_COVERAGE_BLOCKED' ? { blockedByProviderCoverage: true } : {}),
      ...(screeningComplete ? { screeningComplete: true } : {}),
      ...(blockingReason ? { blockingReason } : {}),
      ...(sourceImprovementCount > 0 ? { sourceImprovementCount } : {}),
      ...(report ? { reportId: report.id } : {}),
      ...(report ? { truthQuestionCount: truthQuestionCountOf(report) } : {}),
      // NOT gated on satisfiesGo: the composite scores the type's newest
      // report (discounted) even when it is stale, so the caveat must not
      // vanish the moment a SED bump strips decision-currency (2026-08-22).
      // `auditKind` travels WITH the flag (round 9) so a consumer that puts
      // the flag in a SENTENCE can say whether anything is itemised.
      ...(auditRecommendation
        ? { auditRecommended: true, auditKind: auditRecommendation.kind }
        : {}),
    ...(input.autoRefreshExhaustedByType?.[type] ? { autoRefreshExhausted: true } : {}),
      ...(optionalEvidenceOnly ? { optionalEvidenceOnly: true } : {}),
      ...(savedWithoutCurrentAnalysis ? { savedWithoutCurrentAnalysis: true } : {}),
      ...(analysis
        ? {
            analysisId: analysis.snapshot.analysisId,
            analysisInputHash: analysis.snapshot.inputHash,
            analysisContentHash: analysis.snapshot.contentHash,
            analysisSnapshotPath: analysis.revision.snapshotPath,
            analysisCreatedAt: analysis.snapshot.createdAt,
            analysisInput: analysis.snapshot.input,
            analysisReferenceValid,
            ...(analysisReadiness ? { analysisReadiness } : {}),
          }
        : {}),
      ...(analysisSatisfiesPositiveGate && !hasWithheldNarrative
        ? { decisionEvidenceSource: 'semantic-analysis' as const }
        : contributesToCalculations && calculationReport
          ? { decisionEvidenceSource: 'legacy-report' as const }
          : {}),
      ...(applicability.stage ? { stage: applicability.stage } : {}),
      primaryAction,
      eligibility: {
        group: eligibilityGroup,
        reasonCode: eligibilityReasonCode,
        reason: eligibilityReason,
        nextAction: primaryAction,
      },
      ...(dependencyChain ? { dependencyChain } : {}),
    };
  });
  const markBriefRefreshNeeded = (item: ReportPlanItem, reason: string): void => {
    item.stale = true;
    item.satisfiesGo = false;
    item.calculationStatus = 'saved';
    item.blockingReason = reason;
    const runtime = input.runtimeByType?.['custom-brief'];
    if (runtime === 'queued' || runtime === 'generating') {
      item.lifecycle = runtime;
      item.primaryAction = { kind: 'report-progress', label: 'View analysis progress', reportType: 'custom-brief' };
    } else {
      item.lifecycle = 'refresh-needed';
      item.primaryAction = canEdit
        ? { kind: 'report-setup', label: 'Refresh Custom Brief', reportType: 'custom-brief' }
        : { kind: 'request-access', label: 'Request edit access', destination: 'reports' };
    }
    if (item.eligibility) item.eligibility.nextAction = item.primaryAction;
  };
  if (tracksBriefSources) {
    const acceptedSources = plan.flatMap(item => {
      if (item.type === 'custom-brief' || item.type === 'executive-summary'
        || item.calculationStatus !== 'current' || item.stale || !item.calculationReportId) return [];
      const source = input.reports.find(report => report.siteId === input.site.id && report.id === item.calculationReportId);
      if (!source || !customBriefReportHasSourceBody(source) || !customBriefSourceHasImmutableRevision(source)
        || !customBriefSourceMatchesCurrentPlan(source, item, input.analysisGateEnabled)) return [];
      const context: ReportSatisfactionContext = { site: input.site, sed: input.sed, intent: input.intent,
        requiresGenerationImpact: input.requiresGenerationImpact, powerAvailabilityPreferences: input.powerAvailabilityPreferences,
        analysisGateEnabled: input.analysisGateEnabled, ...(item.type === 'test-fit' ? { testFitReadiness } : {}) };
      const domain = decisionAnalysisDomain(item.type);
      const currentInput = domain ? input.currentAnalysisInputsByDomain?.[domain] : undefined;
      return reportIsCurrentCustomBriefSource({ report: source, type: item.type, context,
        planItem: item, currentAnalysisInput: currentInput }) ? [source] : [];
    });
    for (const item of plan) {
      if (item.type !== 'custom-brief' || !item.calculationReportId) continue;
      const brief = input.reports.find(report => report.siteId === input.site.id && report.id === item.calculationReportId);
      if (!brief || customBriefSourcesAreCurrent(brief, input.site.id, acceptedSources)) continue;
      markBriefRefreshNeeded(item, 'The screening or engineering reports used by this brief changed or are no longer current. Regenerate the brief from the current accepted reports.');
    }
  }
  for (const item of plan) {
    if (item.type !== 'custom-brief' || !item.calculationReportId) continue;
    const brief = input.reports.find(report => report.siteId === input.site.id && report.id === item.calculationReportId);
    const latestOutcome = [...(input.outcomes ?? [])]
      .filter(outcome => outcome.siteId === input.site.id && outcome.reportTypeId === 'custom-brief')
      .sort((left, right) => Date.parse(right.finishedAt) - Date.parse(left.finishedAt))[0];
    const publicationStale = latestOutcome?.terminalReason === 'content-not-current'
      && Date.parse(latestOutcome.finishedAt) >= Date.parse(brief?.createdAt ?? '');
    if (!brief || (!publicationStale
      && customBriefContentMatchesCurrentInputs(brief, input.site, input.sed))) continue;
    markBriefRefreshNeeded(item, 'Site evidence, planning inputs or the saved Brief questions changed. Refresh the Custom Brief to use the current inputs and accepted reports.');
  }
  return plan.filter(item => requestedTypes.includes(item.type));
}

/** Analysis-first entry point; preserves the legacy report-plan implementation. */
export function buildAnalysisPlan(input: BuildReportPlanInput): AnalysisPlanItem[] {
  return buildReportPlan({
    ...input,
    includeFullGeneratedCatalog: true,
  });
}
